import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = signed out, object = signed in
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        // If we're returning from Google OAuth, let AuthCallback exchange the
        // session_id first; don't 401 ourselves on the way through.
        if (window.location.hash?.includes("session_id=")) {
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.twofa_required) {
            // Don't set user yet — caller must complete /auth/2fa/verify.
            return data;
        }
        if (data.token) localStorage.setItem("s2s_token", data.token);
        setUser(data);
        return data;
    };

    const verify2fa = async (ticket, code) => {
        const { data } = await api.post("/auth/2fa/verify", { ticket, code });
        if (data.token) localStorage.setItem("s2s_token", data.token);
        setUser(data);
        return data;
    };

    const setup2fa = async () => {
        const { data } = await api.post("/auth/2fa/setup");
        return data;
    };

    const enable2fa = async (code) => {
        const { data } = await api.post("/auth/2fa/enable", { code });
        await refresh();
        return data;
    };

    const disable2fa = async (code) => {
        const { data } = await api.post("/auth/2fa/disable", { code });
        await refresh();
        return data;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        if (data.token) localStorage.setItem("s2s_token", data.token);
        setUser(data);
        return data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // Cookie/server logout failure is non-fatal — we still clear the
            // local token below so the user appears signed out.
            if (process.env.NODE_ENV === "development") {
                // eslint-disable-next-line no-console
                console.debug("server logout failed (clearing local session anyway):", e?.message);
            }
        }
        localStorage.removeItem("s2s_token");
        setUser(false);
    };

    const deleteAccount = async () => {
        await api.delete("/auth/me");
        localStorage.removeItem("s2s_token");
        setUser(false);
    };

    return (
        <AuthContext.Provider
            value={{ user, login, register, logout, refresh, deleteAccount, verify2fa, setup2fa, enable2fa, disable2fa }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
